import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useUserStore } from '@client/new/stores/user.store';
import { updateSignInEmail } from '@client/new/api/auth.api';

export const STEP_NAME = {
  EMAIL_PROMPT: 'EMAIL_PROMPT',
  CODE_VERIFICATION: 'CODE_VERIFICATION',
} as const;

export type StepName = (typeof STEP_NAME)[keyof typeof STEP_NAME];

type Step =
  | { name: typeof STEP_NAME.EMAIL_PROMPT }
  | { name: typeof STEP_NAME.CODE_VERIFICATION; email: string };

const initialStep: Step = { name: STEP_NAME.EMAIL_PROMPT };

type Params = {
  isOpen: boolean;
  onClose: () => void;
  onAbandon: (step: StepName) => void;
  onSuccess?: (email: string) => unknown;
};

export const useUpdateSignInEmailFlow = ({
  isOpen,
  onClose,
  onAbandon,
  onSuccess,
}: Params) => {
  const [step, setStep] = React.useState<Step>(initialStep);
  const { user, login } = useUserStore();

  const resetFlow = React.useCallback(() => setStep(initialStep), []);

  const goToCodeVerification = React.useCallback(
    (email: string) => setStep({ name: STEP_NAME.CODE_VERIFICATION, email }),
    [],
  );

  React.useEffect(() => {
    if (!isOpen) resetFlow();
  }, [isOpen, resetFlow]);

  const handleClose = React.useCallback(() => {
    onAbandon(step.name);
    resetFlow();
    onClose();
  }, [onAbandon, step.name, onClose, resetFlow]);

  const commitEmailChange = React.useCallback(
    async (email: string, token: string) => {
      try {
        await updateSignInEmail(email, token);
      } catch (error) {
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;
        if (status === 400) {
          toast.error('Email is the same as the current one');
        } else if (status === 401) {
          toast.error('Session expired, please start again');
        } else if (status === 409) {
          toast.error('Email already in use');
        } else {
          toast.error('Failed to update email');
        }
        resetFlow();
        return;
      }

      if (user) login({ ...user, email });
      onSuccess?.(email);
      onClose();
      toast.success('Sign-in email updated');
    },
    [onSuccess, user, login, onClose, resetFlow],
  );

  return {
    step,
    goToCodeVerification,
    resetFlow,
    handleClose,
    commitEmailChange,
  };
};
